import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { functions, db } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import {
  Monitor,
  Loader2,
  LogOut,
  Factory,
  AlertTriangle,
  PlayCircle,
  Timer,
  CheckCircle2
} from 'lucide-react';

const STATUS_PROCESSADO = ['processado', 'concluido', 'enviado'];

/**
 * Contagens de carrinhos/bateladas no setor (mesma regra do kiosk):
 * - disponiveis: prontos para iniciar neste setor
 * - andamento: já iniciados / em processamento
 * - processadas: já passaram/concluíram neste setor (produção do dia)
 */
function contarFluxoSetor(setorId, carrinhos, ordemIdsDia) {
  let disponiveis = 0;
  let andamento = 0;
  let processadas = 0;

  for (const c of carrinhos || []) {
    if (ordemIdsDia && ordemIdsDia.size > 0) {
      const oid = c.ordem_id;
      if (oid && !ordemIdsDia.has(oid)) continue;
    }
    const rota = c.rota_setores || [];
    const idx = rota.findIndex((r) => r.setor_id === setorId);
    if (idx < 0) continue;
    const etapaAtual = Number(c.etapa_atual_ordem || 0);

    if (etapaAtual === idx && c.setor_atual_id === setorId) {
      if (c.status === 'disponivel') disponiveis += 1;
      else if (c.status === 'em_processamento') andamento += 1;
      else if (STATUS_PROCESSADO.includes(String(c.status || ''))) processadas += 1;
    } else if (etapaAtual > idx) {
      // Já avançou deste setor → conta como processada aqui no dia
      processadas += 1;
    }
  }

  return { disponiveis, andamento, processadas };
}

/**
 * Página de trabalho do operador (tela cheia, sem menu).
 * - 1 setor associado: abre direto o painel do setor.
 * - vários setores: mostra os painéis para escolher, com resumo do fluxo.
 * - nenhum: orienta a procurar o supervisor.
 */
const TrabalhoProducaoPage = () => {
  const navigate = useNavigate();
  const { user, userData, getEmpresaId, logout } = useAuth();
  const empresaId = getEmpresaId();
  const uid = user?.uid || userData?.uid;

  const [setores, setSetores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [carrinhos, setCarrinhos] = useState([]);
  const [ordensDiaIds, setOrdensDiaIds] = useState(null);

  useEffect(() => {
    let ativo = true;
    (async () => {
      if (!empresaId || empresaId === 'default') {
        setErro('Empresa não identificada.');
        setLoading(false);
        return;
      }
      try {
        const r = await httpsCallable(functions, 'listSetores')({
          empresa_id: empresaId,
          apenas_ativos: true
        });
        if (!ativo) return;
        const todos = r.data?.data?.items || [];
        const meus = todos.filter((s) => (s.usuario_ids || []).includes(uid));
        setSetores(meus.length > 0 ? meus : todos);
      } catch (e) {
        if (ativo) setErro(e.message || 'Erro ao carregar setores.');
      } finally {
        if (ativo) setLoading(false);
      }
    })();
    return () => {
      ativo = false;
    };
  }, [empresaId, uid]);

  // Tempo real: carrinhos da empresa (visão geral dos fluxos)
  useEffect(() => {
    if (!empresaId || empresaId === 'default') return undefined;
    const q = query(collection(db, 'Carrinhos_Producao'), where('empresa_id', '==', empresaId));
    const unsub = onSnapshot(
      q,
      (snap) => setCarrinhos(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      () => {}
    );
    return () => unsub();
  }, [empresaId]);

  // Ordens/lotes do dia (para “processadas no dia”)
  useEffect(() => {
    if (!empresaId || empresaId === 'default') return undefined;
    let ativo = true;
    const carregar = async () => {
      try {
        const r = await httpsCallable(functions, 'listOrdensFluxoSetorial')({
          empresa_id: empresaId
        });
        if (!ativo) return;
        const ids = new Set((r.data?.data?.items || []).map((o) => o.id));
        setOrdensDiaIds(ids);
      } catch {
        if (ativo) setOrdensDiaIds(null);
      }
    };
    carregar();
    const t = setInterval(carregar, 60000);
    return () => {
      ativo = false;
      clearInterval(t);
    };
  }, [empresaId]);

  const resumoPorSetor = useMemo(() => {
    const map = {};
    for (const s of setores) {
      map[s.id] = contarFluxoSetor(s.id, carrinhos, ordensDiaIds);
    }
    return map;
  }, [setores, carrinhos, ordensDiaIds]);

  const sair = async () => {
    await logout();
    navigate('/app/login', { replace: true });
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-900 text-white">
        <Loader2 className="mr-2 h-6 w-6 animate-spin" /> Carregando sua estação…
      </div>
    );
  }

  if (setores.length === 1) {
    return <Navigate to={`/app/kiosk/${setores[0].id}`} replace />;
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white flex flex-col">
      <header className="flex items-center justify-between px-6 py-4">
        <div className="flex items-center gap-2">
          <Factory className="h-7 w-7 text-emerald-400" />
          <div>
            <h1 className="text-xl font-bold leading-tight">Estação de trabalho</h1>
            <p className="text-xs text-gray-400">{userData?.nome || 'Operador'}</p>
          </div>
        </div>
        <button
          onClick={sair}
          className="flex items-center gap-2 rounded-lg bg-white/10 px-4 py-2 text-sm hover:bg-white/20"
        >
          <LogOut className="h-4 w-4" /> Sair
        </button>
      </header>

      <main className="mx-auto w-full max-w-4xl flex-1 p-6">
        {erro && (
          <div className="mb-4 rounded-lg border border-red-400 bg-red-500/10 px-4 py-2 text-sm text-red-200">
            {erro}
          </div>
        )}

        {setores.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-2xl border border-white/10 bg-white/5 p-10 text-center">
            <AlertTriangle className="mb-3 h-10 w-10 text-amber-400" />
            <p className="text-lg font-semibold">Nenhum setor disponível</p>
            <p className="mt-1 text-sm text-gray-400">
              Procure o supervisor para associar você a um setor de produção.
            </p>
          </div>
        ) : (
          <>
            <p className="mb-4 text-sm text-gray-400">
              Toque no setor para abrir o painel. Os números incluem ordens do dia e de dias anteriores ainda abertas.
            </p>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {setores.map((s) => {
                const r = resumoPorSetor[s.id] || {
                  disponiveis: 0,
                  andamento: 0,
                  processadas: 0
                };
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => navigate(`/app/kiosk/${s.id}`)}
                    className="flex flex-col items-stretch gap-4 rounded-2xl border border-white/10 bg-white/5 p-6 text-left hover:border-emerald-400 hover:bg-white/10"
                  >
                    <div className="flex flex-col items-center gap-2 text-center">
                      <Monitor className="h-9 w-9 text-emerald-400" />
                      <span className="text-lg font-semibold">{s.nome}</span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 border-t border-white/10 pt-3">
                      <div className="flex flex-col items-center gap-1" title="Disponíveis para iniciar">
                        <PlayCircle className="h-5 w-5 text-emerald-400" />
                        <span className="text-lg font-bold tabular-nums text-emerald-300">
                          {r.disponiveis}
                        </span>
                      </div>
                      <div className="flex flex-col items-center gap-1" title="Em andamento">
                        <Timer className="h-5 w-5 text-sky-400" />
                        <span className="text-lg font-bold tabular-nums text-sky-300">{r.andamento}</span>
                      </div>
                      <div className="flex flex-col items-center gap-1" title="Processadas no dia">
                        <CheckCircle2 className="h-5 w-5 text-violet-300" />
                        <span className="text-lg font-bold tabular-nums text-violet-200">
                          {r.processadas}
                        </span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </>
        )}
      </main>

      {setores.length > 0 && (
        <footer className="border-t border-white/10 px-6 py-4">
          <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">Legenda</p>
          <ul className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-gray-300">
            <li className="inline-flex items-center gap-2">
              <PlayCircle className="h-4 w-4 text-emerald-400" />
              Disponíveis para iniciar neste setor
            </li>
            <li className="inline-flex items-center gap-2">
              <Timer className="h-4 w-4 text-sky-400" />
              Em andamento (já iniciadas)
            </li>
            <li className="inline-flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-violet-300" />
              Já processadas neste setor no dia
            </li>
          </ul>
        </footer>
      )}
    </div>
  );
};

export default TrabalhoProducaoPage;
